import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import Text from "components/Text";
import styles from "../AddEvent.module.scss";
import crossIcon from "assets/images/cross.svg";
import StaffDefaultImage from "assets/images/staff-default-rounded.png";
import { withTranslation } from "react-i18next";
import { useAllActiveOffices } from "repositories/scheduler-repository";
import constants from "../../../../constants";

function SelectOfficeModal({
  t,
  isOpen,
  closeStepOneModal,
  ownerId,
  setMemberIds,
  memberIds,
  forEdit,
  // enable if you want multiple selection of offices
  fromSelectOffice,
  // all below variables related to shift module when user wanted to submit without staff selection
  selectOfficeForEvent,
  setSelectOfficeForEvent,
  eventCapsKey,
  callConfirm,
  eventDetail,
  canAddShift = false,
  busySlotOfficeListIds = [],
  isFromBusySlotDeleteList = false,
}) {
  // State management
  const [search, setSearch] = useState("");
  const [officeList, setOfficeList] = useState([]);

  // Track selected office IDs in a simple array
  const [selectedOfficeIds, setSelectedOfficeIds] = useState([]);

  const { isLoading: officeDataLoading, data: officeData } =
    useAllActiveOffices(1, constants.ACTIVE_OFFICES_PAGE_SIZE, {
      ownerId,
      includeAdminOffice: canAddShift,
    });

  // Process office data when it's loaded
  useEffect(() => {
    if (!officeDataLoading && officeData?.data?.length) {
      const filteredData =
        (isFromBusySlotDeleteList &&
          officeData?.data?.filter((item) =>
            busySlotOfficeListIds.includes(item?.id),
          )) ||
        [];
      const finalData = isFromBusySlotDeleteList
        ? filteredData
        : officeData?.data;
      setOfficeList(finalData);
    }
  }, [officeDataLoading, officeData]);

  // Reset form and initialize selection when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset search text
      setSearch("");

      // Initialize selectedOfficeIds from memberIds (only non-deleted ones)
      if (memberIds?.length) {
        const initialSelectedIds = memberIds
          .filter((member) => !member.isDeleted)
          .map((member) => member.officeId);
        setSelectedOfficeIds(initialSelectedIds);
      } else {
        setSelectedOfficeIds([]);
      }

      // Process data if available
      if (officeData?.data?.length && officeList.length <= 1) {
        const filteredData =
          (isFromBusySlotDeleteList &&
            officeData?.data?.filter((item) =>
              busySlotOfficeListIds.includes(item?.id),
            )) ||
          [];
        const finalData = isFromBusySlotDeleteList
          ? filteredData
          : officeData?.data;
        setOfficeList(finalData);
      }
    }
  }, [isOpen, officeData, memberIds]);

  const onSearch = (val) => {
    setSearch(val);
    const value = val.trim();
    if (!value && officeData?.data) {
      const filteredData =
        (isFromBusySlotDeleteList &&
          officeData?.data?.filter((item) =>
            busySlotOfficeListIds.includes(item?.id),
          )) ||
        [];
      const finalData = isFromBusySlotDeleteList
        ? filteredData
        : officeData?.data;
      setOfficeList(finalData);
      return;
    }

    if (!officeData?.data) return;

    const trimmedSearchValue = val.replace(/\s+/g, "").toLowerCase();

    const filterArray = officeList.filter((item) => {
      const fullName = item?.name.replace(/\s+/g, "").toLowerCase();
      return fullName.includes(trimmedSearchValue);
    });

    setOfficeList(filterArray);
  };

  const handleSelectUser = (e, office) => {
    const isChecked = e.currentTarget.checked;

    if (isChecked) {
      // Add office ID to selection if not already there
      setSelectedOfficeIds((prev) =>
        prev.includes(office.id) ? prev : [...prev, office.id],
      );
    } else {
      // Remove office ID from selection
      setSelectedOfficeIds((prev) => prev.filter((id) => id !== office.id));
    }
  };

  const handleAllChange = (e) => {
    if (e.currentTarget.checked) {
      // Select all offices from current list
      const allIds = officeList.map((office) => office.id);
      setSelectedOfficeIds(allIds);
    } else {
      // Deselect all
      setSelectedOfficeIds([]);
    }
  };

  const handleAddEmployees = () => {
    if (!fromSelectOffice) {
      callConfirm();
      return;
    }
    // Create final memberIds array
    const finalMemberIds = [];

    // First, include all originally selected memberIds that are now deselected, marked as deleted
    if (memberIds?.length) {
      memberIds.forEach((member) => {
        // If a member from the original list is no longer selected, mark it as deleted
        if ("externalId" in member && !selectedOfficeIds.includes(member.id)) {
          finalMemberIds.push({
            ...member,
            isDeleted: true,
          });
        }
      });
    }

    // Next, add all currently selected offices
    selectedOfficeIds.forEach((officeId) => {
      const office = officeList.find((o) => o.id === officeId);
      if (office) {
        // Check if this is a new selection or was in the original memberIds
        const existingMember = memberIds?.find((m) => m.id === officeId);

        if (existingMember) {
          // This was in original selection, add with isDeleted=false
          finalMemberIds.push({
            ...existingMember,
            isDeleted: false,
          });
        } else {
          // This is a new selection, add with id=0 for new offices
          let obj = {
            ...office,
            isDeleted: false,
            officeId: office?.id,
          };

          if (forEdit) {
            obj.id = 0; // Set id=0 for new offices
          }
          finalMemberIds.push(obj);
        }
      }
    });

    setMemberIds(finalMemberIds);
    closeStepOneModal(false);
  };
  const typeText = useMemo(
    () => (fromSelectOffice ? "checkbox" : "radio"),
    [fromSelectOffice],
  );
  useEffect(() => {
    if (
      !fromSelectOffice &&
      !selectOfficeForEvent &&
      forEdit &&
      eventDetail?.id
    ) {
      const value = officeList.find((res) => res.id === eventDetail?.officeId);
      if (value) {
        setSelectOfficeForEvent(value);
      }
    }
  }, [fromSelectOffice, officeList]);
  return (
    <Modal
      isOpen={isOpen}
      toggle={() => closeStepOneModal(false)}
      className="modal-dialog-centered modal-width-660 new-group-modal"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={() => closeStepOneModal(false)}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody>
        {/* Header */}
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          {t(
            `accountOwner.${
              fromSelectOffice ? "selectOffices" : "selectOfficeForShift"
            }`,
          )}
        </Text>

        {/* Loading indicator */}
        {officeDataLoading && (
          <div className="loader-container">
            <Spinner animation="border" className="loader-spinner" />
          </div>
        )}
        {!fromSelectOffice && (
          <span>{t("accountOwner.selectOfficeShiftModalDescription")}</span>
        )}
        {fromSelectOffice && (
          <>
            {/* Search and filters */}
            <div className={"search-box w-100 " + styles["search-box"]}>
              <input
                type="text"
                placeholder={t("accountOwner.searchByName")}
                value={search}
                onChange={(e) => onSearch(e.currentTarget.value)}
              />
              <span className="ico">
                <img
                  src={require("assets/images/search-icon.svg").default}
                  alt="icon"
                />
              </span>
            </div>
            {/* Selection counter */}
            <Text
              size="12px"
              marginBottom="20px"
              marginTop="20px"
              weight="400"
              color="rgb(111, 119, 136)"
            >
              {selectedOfficeIds.length} {t("Selected")}
            </Text>

            {/* Select all checkbox */}
            {officeList?.length > 0 && (
              <div className="ch-checkbox c-field all-event-checkbox m-0">
                <label>
                  <input
                    type="checkbox"
                    onChange={handleAllChange}
                    checked={
                      officeList.length > 0 &&
                      selectedOfficeIds.length === officeList.length
                    }
                  />
                  <span>{t("staff.selectAll")}</span>
                </label>
              </div>
            )}
          </>
        )}

        {/* Office list */}
        <ul
          className={
            "modal-employee-list group-list" +
            (fromSelectOffice ? "" : " radio-settings")
          }
          id="scrollableDiv"
        >
          {officeList?.length > 0
            ? officeList.map((item) => {
                return (
                  <li key={item.id}>
                    <div className={`${styles.border_list} ch-${typeText} `}>
                      <label>
                        <input
                          type={typeText}
                          checked={
                            fromSelectOffice
                              ? selectedOfficeIds.includes(item.id)
                              : selectOfficeForEvent?.id === item.id
                          }
                          onChange={(e) => {
                            if (fromSelectOffice) {
                              handleSelectUser(e, item);
                            } else {
                              setSelectOfficeForEvent(item);
                            }
                          }}
                        />
                        <span className="d-flex align-items-center">
                          {fromSelectOffice && (
                            <img
                              src={item?.officeImage || StaffDefaultImage}
                              alt="profile-pic"
                              className={styles.Default_staff_image}
                            />
                          )}
                          <p className="m-0">{item?.name}</p>
                        </span>
                      </label>
                    </div>
                  </li>
                );
              })
            : !officeDataLoading && (
                <li>
                  <div className="ch-checkbox">
                    <label>{t("accountOwner.noOfficeFound")}</label>
                  </div>
                </li>
              )}
        </ul>

        {/* Footer buttons */}
        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100 mt-4"
          title={t("accountOwner.addEmployees")}
          disabled={
            officeList?.length === 0 ||
            (!fromSelectOffice && !selectOfficeForEvent)
          }
          onClick={handleAddEmployees}
        >
          {fromSelectOffice
            ? t("staff.select")
            : t("scheduler.saveEventShift", eventCapsKey)}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3  mt-md-4"
          onClick={() => closeStepOneModal(false)}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(SelectOfficeModal);
